import { relations } from 'drizzle-orm';
import {
  Class, Exercise,
  Group,
  Journey, Unit,
  User,
  Checkpoint, Level, State, Country,
  Audio, Video, Image, Document, TextContent, Content,
  WrittenExercise, RecordedExercise, MultipleChoiceExercise,
  Block, BlockContent,
  EventCategory, Event, ExerciseSubmission,
  Scorecard, ScorecardPresence,
  // Community profile tables
  UserConnection, Badge, UserBadge, Notification, PointTransaction,
  // Community content tables
  Space, Post, Comment, Like, Poll, PollVote,
  DirectMessage, Report, UserBlock, SpaceSubscription, UserBan, UserPinnedSpace, SpaceSubPage, DefaultPinnedSpace, ModeratorAssignment,
  IssueReport,
  FeedbackCampaign, FeedbackCampaignQuestion,
  FeedbackResponse, FeedbackResponseAnswer,
  FeedbackCampaignGroup, FeedbackCampaignUser,
  LearningProgress,
  LearningPath,
  Purchase,
} from './models';
import { boolean, date, integer, pgTable, primaryKey, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core';

// export const UserRelations = relations(User, ({ one, many }) => ({
//   groups: many(UserXGroup),
//   journeys: many(UserXJourney),
//   classes: many(UserXClass),
//   exercises: many(UserXExercise),
//   level: one(Level, {
//     fields: [User.levelId],
//     references: [Level.levelId],
//   }),
// }));
//
// export const GroupRelations = relations(Group, ({ one, many }) => ({
//   users: many(UserXGroup),
//   journeys: many(GroupXJourney),
//   classes: many(GroupXClass),
//   level: one(Level, {
//     fields: [Group.levelId],
//     references: [Level.levelId],
//   }),
// }));
//
// export const JourneyRelations = relations(Journey, ({ one, many }) => ({
//   groups: many(GroupXJourney),
//   units: many(UnitXJourney),
//   users: many(UserXJourney),
//   level: one(Level, {
//     fields: [Journey.levelId],
//     references: [Level.levelId],
//   }),
// }));
//
// export const UnitRelations = relations(Unit, ({ many }) => ({
//   journeys: many(UnitXJourney),
//   classes: many(UnitXClass, {
//     relationName: 'unit_classes',
//   }),
// }));
//
// export const ClassRelations = relations(Class, ({ many }) => ({
//   units: many(UnitXClass, {
//     relationName: 'unit_classes',
//   }),
//   exercises: many(ClassXExercise),
//   users: many(UserXClass),
// }));
//
// export const ExerciseRelations = relations(Exercise, ({ one, many }) => ({
//   checkpoints: many(CheckpointXExercise),
//   classes: many(ClassXExercise),
//   users: many(UserXExercise),
//   exerciseType: one(ExerciseType, {
//     fields: [Exercise.exerciseTypeId],
//     references: [ExerciseType.exerciseTypeId]
//   })
// }));
//
// export const StateRelations = relations(State, ({ one }) => ({
//   country: one(Country, {
//     fields: [State.countryId],
//     references: [Country.countryId]
//   })
// }));


// Many-to-Many Tables
// Note: UserXGroup is defined in models.ts to avoid circular imports with schemas.ts

export const USER_JOURNEY_SOURCES = ["teacher", "path", "purchase", "admin"] as const;
export type UserJourneySource = (typeof USER_JOURNEY_SOURCES)[number];

// One row per (user, journey): the student's access to that journey, whatever
// granted it (`source`). `active` marks the single journey the student is on
// (partial unique index `user_journeys_one_active_per_user_idx`).
export const UserXJourney = pgTable("user_journeys", {
  userId: uuid("user_id").references(() => User.userId).notNull(),
  journeyId: uuid("journey_id").references(() => Journey.journeyId).notNull(),
  assignedBy: text("assigned_by").$type<"teacher" | "self">().notNull().default("self"),
  // Who/what granted access. Write 'teacher' to assignedBy alongside it.
  source: text("source").$type<UserJourneySource>().notNull().default("teacher"),
  active: boolean("active").default(false).notNull(),
  status: text("status").$type<"not_started" | "in_progress" | "completed" | "dropped">().default("not_started").notNull(),
  startDate: timestamp("start_date"),
  completionDate: timestamp("completion_date"),
  lastAccessDate: timestamp("last_access_date"),
  // Access window. NULL endsAt = never expires.
  startsAt: timestamp("starts_at", { withTimezone: true }),
  endsAt: timestamp("ends_at", { withTimezone: true }),
  // Purchase that granted access; kept as history, so deleting the purchase
  // only clears the link.
  purchaseId: uuid("purchase_id").references(() => Purchase.purchaseId, { onDelete: 'set null' }),
  order: integer("order").default(0).notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.userId, table.journeyId] }),
}));

export type UserXJourneySelect = typeof UserXJourney.$inferSelect;

export const UserXClass = pgTable("user_classes", {
  userId: uuid("user_id").references(() => User.userId).notNull(),
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }).notNull(),
  dateStarted: date("date_started"),
  dateFinished: date("date_finished"),
  percentageCompleted: integer("percentage_completed").default(0),
  lastAccessDate: timestamp("last_access_date"),
  status: text("status").$type<"not_started" | "in_progress" | "completed">().default("not_started").notNull(),
});

// Teacher feedback on submissions
export const ExerciseSubmissionFeedback = pgTable("exercise_submission_feedback", {
  feedbackId: uuid("id").defaultRandom().primaryKey().notNull(),
  submissionId: uuid("submission_id").references(() => ExerciseSubmission.submissionId, { onDelete: 'cascade' }).notNull(),
  graderId: uuid("grader_id").references(() => User.userId).notNull(), // Teacher who graded

  gradedAt: timestamp("graded_at", { mode: "string" }).defaultNow().notNull(),

  // Feedback content
  overallComment: text("overall_comment"),
  feedbackType: text("feedback_type").$type<"auto" | "manual" | "mixed">().notNull(),

  // Detailed feedback (optional, for complex exercises)
  detailedFeedback: text("detailed_feedback"), // JSON field for line-by-line, rubric-based, etc.
});

export type ExerciseSubmissionFeedbackSelect = typeof ExerciseSubmissionFeedback.$inferSelect;

// Media attachments for submissions (audio recordings, images, documents)
export const ExerciseSubmissionMedia = pgTable("exercise_submission_media", {
  mediaId: uuid("id").defaultRandom().primaryKey().notNull(),
  submissionId: uuid("submission_id").references(() => ExerciseSubmission.submissionId, { onDelete: 'cascade' }).notNull(),

  mediaType: text("media_type").$type<"audio" | "video" | "image" | "document">().notNull(),
  url: text("url").notNull(),
  mimeType: text("mime_type"),
  fileSize: integer("file_size"),
  duration: integer("duration"), // For audio/video in seconds

  uploadedAt: timestamp("uploaded_at", { mode: "string" }).defaultNow().notNull(),
});

export type ExerciseSubmissionMediaSelect = typeof ExerciseSubmissionMedia.$inferSelect;

export const GroupXJourney = pgTable("group_journeys", {
  groupId: uuid("group_id").references(() => Group.groupId),
  journeyId: uuid("journey_id").references(() => Journey.journeyId),
  active: boolean("active").default(false).notNull(),
  startDate: date("start_date"),
  endDate: date("end_date"),
  order: integer("order").default(0).notNull(),
});

// Ordered journeys of a learning path. A journey may appear once per path;
// a level may appear several times (e.g. a reinforcement journey).
export const LearningPathXJourney = pgTable("learning_path_journeys", {
  pathId: uuid("path_id").references(() => LearningPath.pathId, { onDelete: 'cascade' }).notNull(),
  journeyId: uuid("journey_id").references(() => Journey.journeyId).notNull(),
  order: integer("order").notNull(),
}, (table) => ({
  pk: primaryKey({ columns: [table.pathId, table.journeyId] }),
  uniqueOrder: uniqueIndex("learning_path_journeys_path_order_unique").on(table.pathId, table.order),
}));

export type LearningPathXJourneySelect = typeof LearningPathXJourney.$inferSelect;

export const UnitXJourney = pgTable("unit_journeys", {
  unitId: uuid("unit_id").references(() => Unit.unitId).notNull(),
  journeyId: uuid("journey_id").references(() => Journey.journeyId).notNull(),
  order: integer("order").notNull().default(0), // Customizable order
});

export const UnitXClass = pgTable("unit_classes", {
  unitId: uuid("unit_id").references(() => Unit.unitId, { onDelete: 'cascade' }),
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }),
  order: integer("order").notNull().default(0),
});

export const ClassXExercise = pgTable("class_exercises", {
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }),
  exerciseId: uuid("exercise_id").references(() => Exercise.exerciseId),
});

export const CheckpointXExercise = pgTable("checkpoint_exercises", {
  exerciseId: uuid("exercise_id").references(() => Exercise.exerciseId),
  checkpointId: uuid("checkpoint_id").references(() => Checkpoint.checkpointId),
});

export const GroupXClass = pgTable("group_classes", {
  groupId: uuid("group_id").references(() => Group.groupId),
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }),
  dateStarted: timestamp("date_started").notNull(),
  dateFinished: timestamp("date_finished"), // Nullable for in-progress classes
});

// export const UnitXClassRelations = relations(UnitXClass, ({ one }) => ({
//   unit: one(Unit, {
//     fields: [UnitXClass.unitId],
//     references: [Unit.unitId],
//   }),
//   contract: one(Class, {
//     fields: [UnitXClass.classId],
//     references: [Class.classId],
//   }),
// }));

// Relationship: Exercises <-> Videos
export const ExerciseXVideo = pgTable("exercise_videos", {
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId)
    .notNull(),
  videoId: uuid("video_id")
    .references(() => Video.videoId)
    .notNull(),
});

// Relationship: Exercises <-> Images
export const ExerciseXImage = pgTable("exercise_images", {
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId)
    .notNull(),
  imageId: uuid("image_id")
    .references(() => Image.imageId)
    .notNull(),
});

// Relationship: Exercises <-> Audios
export const ExerciseXAudio = pgTable("exercise_audios", {
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId)
    .notNull(),
  audioId: uuid("audio_id")
    .references(() => Audio.audioId)
    .notNull(),
});

// Relationship: Exercises <-> Documents
export const ExerciseXDocument = pgTable("exercise_documents", {
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId)
    .notNull(),
  documentId: uuid("document_id")
    .references(() => Document.documentId)
    .notNull(),
});

export const ExerciseXMultipleChoice = pgTable("exercise_multiple_choice", {
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId)
    .notNull()
    .primaryKey(), // One-to-one relationship
  multipleChoiceExerciseId: uuid("multiple_choice_exercise_id")
    .references(() => MultipleChoiceExercise.multipleChoiceExerciseId)
    .notNull(),
});

export const ExerciseXWritten = pgTable("exercise_written", {
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId)
    .notNull()
    .primaryKey(), // One-to-one relationship
  writtenExerciseId: uuid("written_exercise_id")
    .references(() => WrittenExercise.writtenExerciseId)
    .notNull(),
});

export const ExerciseXRecorded = pgTable("exercise_recorded", {
  exerciseId: uuid("exercise_id")
    .references(() => Exercise.exerciseId)
    .notNull()
    .primaryKey(), // One-to-one relationship
  recordedExerciseId: uuid("recorded_exercise_id")
    .references(() => RecordedExercise.recordedExerciseId)
    .notNull(),
});

export const UserXUnit = pgTable("user_units", {
  userId: uuid("user_id").references(() => User.userId).notNull(),
  unitId: uuid("unit_id").references(() => Unit.unitId).notNull(),
  enrollmentType: text("enrollment_type").$type<"group" | "self">().notNull().default("self"),
  status: text("status").$type<"not_started" | "in_progress" | "completed">().default("not_started").notNull(),
  dateStarted: timestamp("date_started"),
  dateFinished: timestamp("date_finished"),
  progressPercentage: integer("progress_percentage").default(0),
});

export const GroupXUnit = pgTable("group_units", {
  groupId: uuid("group_id")
    .references(() => Group.groupId)
    .notNull(),
  unitId: uuid("unit_id")
    .references(() => Unit.unitId)
    .notNull(),
  dateStarted: timestamp("date_started").notNull(),
  dateFinished: timestamp("date_finished"), // Nullable for in-progress units
});

// Note: Exercise submission media is now handled by ExerciseSubmissionMedia table
// which stores URLs to media files (audio, video, image, document) uploaded as part of submissions

export const EventAttendee = pgTable("event_attendees", {
  eventId: uuid("event_id").references(() => Event.eventId, { onDelete: 'cascade' }).notNull(),
  userId: uuid("user_id").references(() => User.userId, { onDelete: 'cascade' }).notNull(),
  role: text("role").$type<"student" | "co_host" | "guest" | "observer">().default("student").notNull(),
  status: text("status")
    .$type<"invited" | "confirmed" | "waitlisted" | "attended" | "absent">()
    .default("invited")
    .notNull(),
  createdAt: timestamp("created_at", { mode: 'string' }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: 'string' }).defaultNow().notNull(),
});

export type EventAttendeeSelect = typeof EventAttendee.$inferSelect;

export const EventGroup = pgTable("event_groups", {
  eventId: uuid("event_id").references(() => Event.eventId, { onDelete: 'cascade' }).notNull(),
  groupId: uuid("group_id").references(() => Group.groupId, { onDelete: 'cascade' }).notNull(),
  accessType: text("access_type").$type<"exclusive" | "optional">().default("exclusive").notNull(),
  createdAt: timestamp("created_at", { mode: 'string' }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: 'string' }).defaultNow().notNull(),
});

export type EventGroupSelect = typeof EventGroup.$inferSelect;

export const EventClass = pgTable("event_classes", {
  eventId: uuid("event_id").references(() => Event.eventId, { onDelete: 'cascade' }).notNull(),
  classId: uuid("class_id").references(() => Class.classId, { onDelete: 'cascade' }).notNull(),
  createdAt: timestamp("created_at", { mode: 'string' }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { mode: 'string' }).defaultNow().notNull(),
});

export type EventClassSelect = typeof EventClass.$inferSelect;

// Content relationships
export const ContentRelations = relations(Content, ({ one, many }) => ({
  textContent: one(TextContent, {
    fields: [Content.contentId],
    references: [TextContent.contentId],
    relationName: 'content_text_content',
  }),
  image: one(Image, {
    fields: [Content.contentId],
    references: [Image.contentId],
    relationName: 'content_image',
  }),
  video: one(Video, {
    fields: [Content.contentId],
    references: [Video.contentId],
    relationName: 'content_video',
  }),
  audio: one(Audio, {
    fields: [Content.contentId],
    references: [Audio.contentId],
    relationName: 'content_audio',
  }),
  document: one(Document, {
    fields: [Content.contentId],
    references: [Document.contentId],
    relationName: 'content_document',
  }),
  exercise: one(Exercise, {
    fields: [Content.contentId],
    references: [Exercise.contentId],
    relationName: 'content_exercise',
  }),
  blockContents: many(BlockContent),
}));

export const EventCategoryRelations = relations(EventCategory, ({ many }) => ({
  events: many(Event),
}));

// Relationships for content subtypes to Content
export const TextContentRelations = relations(TextContent, ({ one }) => ({
  content: one(Content, {
    fields: [TextContent.contentId],
    references: [Content.contentId],
    relationName: 'content_text_content',
  }),
}));

export const ImageRelations = relations(Image, ({ one }) => ({
  content: one(Content, {
    fields: [Image.contentId],
    references: [Content.contentId],
    relationName: 'content_image',
  }),
}));

export const VideoRelations = relations(Video, ({ one }) => ({
  content: one(Content, {
    fields: [Video.contentId],
    references: [Content.contentId],
    relationName: 'content_video',
  }),
}));

export const AudioRelations = relations(Audio, ({ one }) => ({
  content: one(Content, {
    fields: [Audio.contentId],
    references: [Content.contentId],
    relationName: 'content_audio',
  }),
}));

export const DocumentRelations = relations(Document, ({ one }) => ({
  content: one(Content, {
    fields: [Document.contentId],
    references: [Content.contentId],
    relationName: 'content_document',
  }),
}));

export const ExerciseRelations = relations(Exercise, ({ one, many }) => ({
  content: one(Content, {
    fields: [Exercise.contentId],
    references: [Content.contentId],
    relationName: 'content_exercise',
  }),
  checkpoints: many(CheckpointXExercise),
  classes: many(ClassXExercise),
  submissions: many(ExerciseSubmission),
}));

// Block relationships
export const BlockRelations = relations(Block, ({ one, many }) => ({
  class: one(Class, {
    fields: [Block.classId],
    references: [Class.classId],
  }),
  blockContents: many(BlockContent),
}));

export const BlockContentRelations = relations(BlockContent, ({ one }) => ({
  block: one(Block, {
    fields: [BlockContent.blockId],
    references: [Block.blockId],
  }),
  content: one(Content, {
    fields: [BlockContent.contentId],
    references: [Content.contentId],
  }),
}));

// Update Class relations to include blocks
export const ClassRelations = relations(Class, ({ many }) => ({
  units: many(UnitXClass, {
    relationName: 'unit_classes',
  }),
  exercises: many(ClassXExercise),
  users: many(UserXClass),
  blocks: many(Block),
  events: many(EventClass),
  // image: one(Image, {
  //   fields: [Class.imageId],
  //   references: [Image.imageId],
  // }),
}));

export const EventRelations = relations(Event, ({ one, many }) => ({
  category: one(EventCategory, {
    fields: [Event.eventCategoryId],
    references: [EventCategory.eventCategoryId],
  }),
  host: one(User, {
    fields: [Event.hostUserId],
    references: [User.userId],
    relationName: 'event_host',
  }),
  createdBy: one(User, {
    fields: [Event.createdByUserId],
    references: [User.userId],
    relationName: 'event_created_by',
  }),
  seriesTemplate: one(Event, {
    fields: [Event.seriesTemplateId],
    references: [Event.eventId],
    relationName: 'event_series_template',
  }),
  seriesOccurrences: many(Event, {
    relationName: 'event_series_template',
  }),
  parentEvent: one(Event, {
    fields: [Event.parentEventId],
    references: [Event.eventId],
    relationName: 'event_parent',
  }),
  childEvents: many(Event, {
    relationName: 'event_parent',
  }),
  attendees: many(EventAttendee),
  groups: many(EventGroup),
  classes: many(EventClass),
}));

export const EventAttendeeRelations = relations(EventAttendee, ({ one }) => ({
  event: one(Event, {
    fields: [EventAttendee.eventId],
    references: [Event.eventId],
  }),
  user: one(User, {
    fields: [EventAttendee.userId],
    references: [User.userId],
  }),
}));

export const EventGroupRelations = relations(EventGroup, ({ one }) => ({
  event: one(Event, {
    fields: [EventGroup.eventId],
    references: [Event.eventId],
  }),
  group: one(Group, {
    fields: [EventGroup.groupId],
    references: [Group.groupId],
  }),
}));

export const EventClassRelations = relations(EventClass, ({ one }) => ({
  event: one(Event, {
    fields: [EventClass.eventId],
    references: [Event.eventId],
  }),
  class: one(Class, {
    fields: [EventClass.classId],
    references: [Class.classId],
  }),
}));

export const ScorecardRelations = relations(Scorecard, ({ one, many }) => ({
  event: one(Event, {
    fields: [Scorecard.eventId],
    references: [Event.eventId],
  }),
  teacher: one(User, {
    fields: [Scorecard.teacherId],
    references: [User.userId],
  }),
  presences: many(ScorecardPresence),
}));

export const ScorecardPresenceRelations = relations(ScorecardPresence, ({ one }) => ({
  scorecard: one(Scorecard, {
    fields: [ScorecardPresence.scorecardId],
    references: [Scorecard.scorecardId],
  }),
  student: one(User, {
    fields: [ScorecardPresence.studentId],
    references: [User.userId],
  }),
}));

// ==========================================
// COMMUNITY PLATFORM RELATIONSHIPS
// ==========================================

// User relationships (for community features)
export const UserRelations = relations(User, ({ many }) => ({
  // Community connections
  following: many(UserConnection, { relationName: 'user_following' }),
  followers: many(UserConnection, { relationName: 'user_followers' }),
  // Badges
  badges: many(UserBadge),
  // Notifications
  notifications: many(Notification),
  // Point transactions
  pointTransactions: many(PointTransaction),
  // Pinned spaces
  pinnedSpaces: many(UserPinnedSpace),
  // Authored sub-pages
  authoredSubPages: many(SpaceSubPage),
  // Moderator assignments
  moderatedSpaces: many(ModeratorAssignment),
}));

// User connection relationships
export const UserConnectionRelations = relations(UserConnection, ({ one }) => ({
  follower: one(User, {
    fields: [UserConnection.followerId],
    references: [User.userId],
    relationName: 'user_following',
  }),
  following: one(User, {
    fields: [UserConnection.followingId],
    references: [User.userId],
    relationName: 'user_followers',
  }),
}));

// Badge relationships
export const BadgeRelations = relations(Badge, ({ many }) => ({
  userBadges: many(UserBadge),
}));

// User badge relationships
export const UserBadgeRelations = relations(UserBadge, ({ one }) => ({
  user: one(User, {
    fields: [UserBadge.userId],
    references: [User.userId],
  }),
  badge: one(Badge, {
    fields: [UserBadge.badgeId],
    references: [Badge.badgeId],
  }),
}));

// Notification relationships
export const NotificationRelations = relations(Notification, ({ one }) => ({
  user: one(User, {
    fields: [Notification.userId],
    references: [User.userId],
  }),
}));

export const IssueReportRelations = relations(IssueReport, ({ one }) => ({
  user: one(User, {
    fields: [IssueReport.userId],
    references: [User.userId],
  }),
}));

// Point transaction relationships
export const PointTransactionRelations = relations(PointTransaction, ({ one }) => ({
  user: one(User, {
    fields: [PointTransaction.userId],
    references: [User.userId],
  }),
}));

// ==========================================
// COMMUNITY CONTENT RELATIONSHIPS
// ==========================================

// Space relationships
export const SpaceRelations = relations(Space, ({ one, many }) => ({
  createdBy: one(User, {
    fields: [Space.createdByAdminId],
    references: [User.userId],
  }),
  posts: many(Post),
  subscriptions: many(SpaceSubscription),
  pinnedBy: many(UserPinnedSpace),
  subPages: many(SpaceSubPage),
  isDefaultPinned: one(DefaultPinnedSpace),
  moderators: many(ModeratorAssignment),
}));

// Post relationships
export const PostRelations = relations(Post, ({ one, many }) => ({
  space: one(Space, {
    fields: [Post.spaceId],
    references: [Space.spaceId],
  }),
  author: one(User, {
    fields: [Post.authorId],
    references: [User.userId],
  }),
  comments: many(Comment),
  poll: one(Poll, {
    fields: [Post.postId],
    references: [Poll.postId],
  }),
}));

// Comment relationships
export const CommentRelations = relations(Comment, ({ one }) => ({
  post: one(Post, {
    fields: [Comment.postId],
    references: [Post.postId],
  }),
  author: one(User, {
    fields: [Comment.authorId],
    references: [User.userId],
  }),
}));

// Poll relationships
export const PollRelations = relations(Poll, ({ one, many }) => ({
  post: one(Post, {
    fields: [Poll.postId],
    references: [Post.postId],
  }),
  votes: many(PollVote),
}));

// Poll vote relationships
export const PollVoteRelations = relations(PollVote, ({ one }) => ({
  poll: one(Poll, {
    fields: [PollVote.pollId],
    references: [Poll.pollId],
  }),
  user: one(User, {
    fields: [PollVote.userId],
    references: [User.userId],
  }),
}));

// Direct message relationships
export const DirectMessageRelations = relations(DirectMessage, ({ one }) => ({
  sender: one(User, {
    fields: [DirectMessage.senderId],
    references: [User.userId],
    relationName: 'dm_sender',
  }),
  receiver: one(User, {
    fields: [DirectMessage.receiverId],
    references: [User.userId],
    relationName: 'dm_receiver',
  }),
}));

// Report relationships
export const ReportRelations = relations(Report, ({ one }) => ({
  reporter: one(User, {
    fields: [Report.reporterId],
    references: [User.userId],
    relationName: 'report_reporter',
  }),
  resolvedBy: one(User, {
    fields: [Report.resolvedById],
    references: [User.userId],
    relationName: 'report_resolved_by',
  }),
}));

// Block relationships (renamed to UserBlockRelations to avoid conflict)
export const UserBlockRelations = relations(UserBlock, ({ one }) => ({
  blocker: one(User, {
    fields: [UserBlock.blockerId],
    references: [User.userId],
    relationName: 'block_blocker',
  }),
  blocked: one(User, {
    fields: [UserBlock.blockedId],
    references: [User.userId],
    relationName: 'block_blocked',
  }),
}));

// User pinned space relationships
export const UserPinnedSpaceRelations = relations(UserPinnedSpace, ({ one }) => ({
  user: one(User, {
    fields: [UserPinnedSpace.userId],
    references: [User.userId],
  }),
  space: one(Space, {
    fields: [UserPinnedSpace.spaceId],
    references: [Space.spaceId],
  }),
}));

// Space sub-page relationships
export const SpaceSubPageRelations = relations(SpaceSubPage, ({ one }) => ({
  space: one(Space, {
    fields: [SpaceSubPage.spaceId],
    references: [Space.spaceId],
  }),
  author: one(User, {
    fields: [SpaceSubPage.authorId],
    references: [User.userId],
  }),
}));

// Default pinned space relationships
export const DefaultPinnedSpaceRelations = relations(DefaultPinnedSpace, ({ one }) => ({
  space: one(Space, {
    fields: [DefaultPinnedSpace.spaceId],
    references: [Space.spaceId],
  }),
}));

// Moderator assignment relationships
export const ModeratorAssignmentRelations = relations(ModeratorAssignment, ({ one }) => ({
  user: one(User, {
    fields: [ModeratorAssignment.userId],
    references: [User.userId],
  }),
  space: one(Space, {
    fields: [ModeratorAssignment.spaceId],
    references: [Space.spaceId],
  }),
}));

// Space subscription relationships
export const SpaceSubscriptionRelations = relations(SpaceSubscription, ({ one }) => ({
  space: one(Space, {
    fields: [SpaceSubscription.spaceId],
    references: [Space.spaceId],
  }),
  user: one(User, {
    fields: [SpaceSubscription.userId],
    references: [User.userId],
  }),
}));

// User ban relationships
export const UserBanRelations = relations(UserBan, ({ one }) => ({
  user: one(User, {
    fields: [UserBan.userId],
    references: [User.userId],
    relationName: 'ban_user',
  }),
  bannedByUser: one(User, {
    fields: [UserBan.bannedBy],
    references: [User.userId],
    relationName: 'ban_admin',
  }),
}));

// ── Feedback Campaign Relations ─────────────────────────────────────────────

export const FeedbackCampaignRelations = relations(FeedbackCampaign, ({ one, many }) => ({
  event: one(Event, {
    fields: [FeedbackCampaign.eventId],
    references: [Event.eventId],
  }),
  createdBy: one(User, {
    fields: [FeedbackCampaign.createdByUserId],
    references: [User.userId],
  }),
  questions: many(FeedbackCampaignQuestion),
  responses: many(FeedbackResponse),
  targetGroups: many(FeedbackCampaignGroup),
  targetUsers: many(FeedbackCampaignUser),
}));

export const FeedbackCampaignQuestionRelations = relations(FeedbackCampaignQuestion, ({ one, many }) => ({
  campaign: one(FeedbackCampaign, {
    fields: [FeedbackCampaignQuestion.campaignId],
    references: [FeedbackCampaign.campaignId],
  }),
  answers: many(FeedbackResponseAnswer),
}));

export const FeedbackResponseRelations = relations(FeedbackResponse, ({ one, many }) => ({
  campaign: one(FeedbackCampaign, {
    fields: [FeedbackResponse.campaignId],
    references: [FeedbackCampaign.campaignId],
  }),
  user: one(User, {
    fields: [FeedbackResponse.userId],
    references: [User.userId],
  }),
  event: one(Event, {
    fields: [FeedbackResponse.eventId],
    references: [Event.eventId],
  }),
  answers: many(FeedbackResponseAnswer),
}));

export const FeedbackResponseAnswerRelations = relations(FeedbackResponseAnswer, ({ one }) => ({
  response: one(FeedbackResponse, {
    fields: [FeedbackResponseAnswer.responseId],
    references: [FeedbackResponse.responseId],
  }),
  question: one(FeedbackCampaignQuestion, {
    fields: [FeedbackResponseAnswer.questionId],
    references: [FeedbackCampaignQuestion.questionId],
  }),
}));

export const FeedbackCampaignGroupRelations = relations(FeedbackCampaignGroup, ({ one }) => ({
  campaign: one(FeedbackCampaign, {
    fields: [FeedbackCampaignGroup.campaignId],
    references: [FeedbackCampaign.campaignId],
  }),
  group: one(Group, {
    fields: [FeedbackCampaignGroup.groupId],
    references: [Group.groupId],
  }),
}));

export const FeedbackCampaignUserRelations = relations(FeedbackCampaignUser, ({ one }) => ({
  campaign: one(FeedbackCampaign, {
    fields: [FeedbackCampaignUser.campaignId],
    references: [FeedbackCampaign.campaignId],
  }),
  user: one(User, {
    fields: [FeedbackCampaignUser.userId],
    references: [User.userId],
  }),
}));

// ── Learning Progress Relations ─────────────────────────────────────────────

export const LearningProgressRelations = relations(LearningProgress, ({ one }) => ({
  user: one(User, { fields: [LearningProgress.userId], references: [User.userId] }),
  journey: one(Journey, { fields: [LearningProgress.journeyId], references: [Journey.journeyId] }),
  unit: one(Unit, { fields: [LearningProgress.unitId], references: [Unit.unitId] }),
  class: one(Class, { fields: [LearningProgress.classId], references: [Class.classId] }),
}));
